/**
 * Standalone Hotels Portal - Guest Arrival Survey
 * 
 * PUBLIC page - No login required.
 * 4-question check-in survey triggered by Guest Arrival NFC cards.
 * Full bilingual support (EN/FR).
 */
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, Loader2, CheckCircle, ThumbsUp, ThumbsDown, Minus, Globe, MessageSquare, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import SurveyLandingPage from './SurveyLandingPage';
import { tLang } from '../../lib/langHelper';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${API_BASE}/api/hotels-standalone`;

export default function StandaloneSurvey() {
  const { t, i18n } = useTranslation();
  const { orgId, locationId } = useParams();
  const [searchParams] = useSearchParams();
  const cardId = searchParams.get('card');
  const langParam = searchParams.get('lang');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState([]);
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [branding, setBranding] = useState({ logo_url: null, logo_size: 'medium' });
  const [showLandingPage, setShowLandingPage] = useState(false);
  const [languageSettings, setLanguageSettings] = useState(null);
  const [reviewRedirect, setReviewRedirect] = useState(null);
  const [showReviewPrompt, setShowReviewPrompt] = useState(false);

  // Logo size mapping
  const logoSizeClasses = {
    small: 'h-12 max-w-[100px]',
    medium: 'h-20 max-w-[160px]',
    large: 'h-28 max-w-[220px]',
    very_large: 'h-36 max-w-[280px]'
  };

  // Force dark mode for standalone pages
  useEffect(() => {
    document.documentElement.classList.add('dark');
    return () => {
      const savedTheme = localStorage.getItem('yul_theme_preference');
      if (savedTheme !== 'dark') {
        document.documentElement.classList.remove('dark');
      }
    };
  }, []);

  // Set language from URL param if provided
  useEffect(() => {
    if (langParam && ['en', 'fr'].includes(langParam)) {
      i18n.changeLanguage(langParam);
    }
  }, [langParam, i18n]);

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    fetchSurvey();
  }, [orgId, locationId]);

  const fetchSurvey = async () => {
    try {
      const url = `${API_URL}/survey/${orgId}/${locationId}?lang=${i18n.language}${cardId ? `&card=${cardId}` : ''}`;
      const response = await fetch(url);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('standalone.survey.errorLoading'));
      }

      setSurveyData(data);
      if (data.branding) setBranding(data.branding);
      
      // Handle language settings
      const ls = data.language_settings || {};
      setLanguageSettings(ls);
      
      // Handle review redirect settings
      if (data.review_redirect) {
        setReviewRedirect(data.review_redirect);
      }
      
      // If landing page enabled and no lang param explicitly set, show landing page
      if (ls.landing_page_enabled && !langParam) {
        setShowLandingPage(true);
      } else if (!langParam && ls.default_lang && ls.default_lang !== i18n.language) {
        // Apply default language if no lang param
        i18n.changeLanguage(ls.default_lang);
      }
      
      setResponses(new Array(data.questions.length).fill(null));
      setLoading(false);
    } catch (err) {
      console.error('[StandaloneSurvey] Error:', err);
      setError(err.message);
      setLoading(false);
    }
  };
  
  const handleLandingLanguageSelect = (lang) => {
    i18n.changeLanguage(lang);
    setShowLandingPage(false);
    // Re-fetch survey in selected language
    setLoading(true);
    const url = `${API_URL}/survey/${orgId}/${locationId}?lang=${lang}${cardId ? `&card=${cardId}` : ''}`;
    fetch(url).then(r => r.json()).then(data => {
      if (data.questions) {
        setSurveyData(data);
        if (data.branding) setBranding(data.branding);
        setResponses(new Array(data.questions.length).fill(null));
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const handleRatingResponse = (rating) => {
    const newResponses = [...responses];
    newResponses[currentQuestion] = {
      question_id: surveyData.questions[currentQuestion].id,
      response_type: 'rating',
      value: rating
    };
    setResponses(newResponses);
    
    // Auto-advance after selection
    setTimeout(() => {
      if (currentQuestion < surveyData.questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      }
    }, 300);
  };

  const handleYesNoResponse = (value) => {
    const newResponses = [...responses];
    newResponses[currentQuestion] = {
      question_id: surveyData.questions[currentQuestion].id,
      response_type: 'yes_no_neutral',
      value: value
    };
    setResponses(newResponses);
    
    // Auto-advance after selection
    setTimeout(() => {
      if (currentQuestion < surveyData.questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      }
    }, 300);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/survey/${orgId}/${locationId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          card_id: cardId,
          responses: responses.filter(r => r !== null),
          notes: notes.trim() || null
        })
      });

      if (!response.ok) {
        throw new Error(t('standalone.survey.errorSubmitting'));
      }

      // Check if all rating responses are 4+ stars for review redirect
      const ratingResponses = responses.filter(r => r && r.response_type === 'rating');
      const allHighRatings = ratingResponses.length > 0 && ratingResponses.every(r => r.value >= 4);
      
      if (allHighRatings && reviewRedirect?.enabled && reviewRedirect?.url) {
        setShowReviewPrompt(true);
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/10 backdrop-blur border-white/20">
          <CardContent className="pt-6 text-center">
            <Loader2 className="w-12 h-12 text-white animate-spin mx-auto mb-4" />
            <p className="text-white text-lg">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show language landing page if enabled
  if (showLandingPage && languageSettings) {
    return (
      <SurveyLandingPage
        branding={branding}
        languageSettings={languageSettings}
        organizationName={surveyData?.organization?.name}
        onSelectLanguage={handleLandingLanguageSelect}
        apiBase={API_BASE}
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardContent className="pt-6 text-center">
            <p className="text-red-600 text-lg font-medium mb-2">{t('common.error')}</p>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show review redirect prompt for high ratings
  if (showReviewPrompt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl" data-testid="review-redirect-prompt">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Star className="w-10 h-10 text-amber-500 fill-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {tLang(i18n.language, 'Thank you for your feedback!', 'Merci pour votre avis!', '¡Gracias por sus comentarios!')}
            </h1>
            <p className="text-gray-600 mb-6">
              {tLang(i18n.language, 'Would you kindly leave us a review on Google?', 'Pourriez-vous nous laisser un avis sur Google?', 'Would you kindly leave us a review on Google?')}
            </p>
            <div className="space-y-3">
              <Button
                className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3"
                onClick={() => window.open(reviewRedirect.url, '_blank')}
                data-testid="leave-review-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {tLang(i18n.language, 'Leave a Review', 'Laisser un avis', 'Dejar una reseña')}
              </Button>
              <Button
                variant="ghost"
                className="w-full text-gray-500 hover:text-gray-700"
                onClick={() => { setShowReviewPrompt(false); setSubmitted(true); }}
                data-testid="no-thanks-btn"
              >
                {tLang(i18n.language, 'No, thanks', 'Non, merci', 'No, gracias')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white shadow-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('standalone.survey.thankYou')}</h1>
            <p className="text-gray-600 mb-2">{surveyData?.organization?.name}</p>
            <p className="text-gray-500 text-sm">{t('standalone.survey.thankYouMessage')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const question = surveyData?.questions?.[currentQuestion];
  const progress = ((currentQuestion + 1) / surveyData?.questions?.length) * 100;

  // Get translated question text
  const getQuestionText = (q) => {
    if (!q) return '';
    
    // If text is an object with language keys
    if (q.text && typeof q.text === 'object') {
      const lang = i18n.language;
      if (lang === 'fr') return q.text.fr || q.text.en;
      if (lang === 'es') return q.text.es || q.text.en;
      return q.text.en || q.text.fr;
    }
    
    // Legacy format with separate text_fr/text_en fields
    if (i18n.language === 'fr' && q.text_fr) {
      return q.text_fr;
    }
    if (i18n.language === 'es' && q.text_es) {
      return q.text_es;
    }
    
    return q.text || q.text_en || t(`standalone.survey.questions.q${currentQuestion + 1}`);
  };

  // Debug: Log branding state on every render
  console.log('[StandaloneSurvey] Current branding state:', branding);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-4">
      {/* Language Toggle */}
      <div className="absolute top-4 right-4">
        <LanguageSelector variant="compact" />
      </div>

      <Card className="w-full max-w-md bg-white shadow-2xl">
        <CardContent className="pt-6">
          {/* Header with Logo */}
          <div className="text-center mb-6">
            {branding.logo_url && (
              <div className="flex justify-center mb-4">
                <img 
                  src={branding.logo_url.startsWith('http') ? branding.logo_url : `${API_BASE}${branding.logo_url}`}
                  alt={surveyData?.organization?.name || 'Logo'}
                  className={`object-contain ${logoSizeClasses[branding.logo_size] || logoSizeClasses.medium}`}
                  onError={(e) => { 
                    console.error('[StandaloneSurvey] Logo failed to load:', branding.logo_url);
                    e.target.style.display = 'none'; 
                  }}
                  onLoad={() => console.log('[StandaloneSurvey] Logo loaded successfully')}
                />
              </div>
            )}
            <h1 className="text-xl font-bold text-gray-900">{surveyData?.organization?.name}</h1>
            <p className="text-sm text-gray-500">{surveyData?.location?.name || 'Main Location'} • {t('standalone.survey.title')}</p>
          </div>

          {/* Progress */}
          <div className="mb-6">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>{t('standalone.survey.progress', { current: currentQuestion + 1, total: surveyData?.questions?.length })}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Question */}
          <div className="mb-8">
            <h2 className="text-lg font-medium text-gray-900 text-center mb-6">
              {getQuestionText(question)}
            </h2>

            {/* Rating Response */}
            {question?.response_type === 'rating' && (
              <div className="flex justify-center space-x-2">
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => handleRatingResponse(rating)}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                      responses[currentQuestion]?.value === rating
                        ? 'bg-green-500 text-white scale-110'
                        : 'bg-gray-100 text-gray-400 hover:bg-green-100 hover:text-green-500'
                    }`}
                  >
                    <Star className={`w-6 h-6 ${responses[currentQuestion]?.value >= rating ? 'fill-current' : ''}`} />
                  </button>
                ))}
              </div>
            )}

            {/* Yes/No/Neutral Response */}
            {question?.response_type === 'yes_no_neutral' && (
              <div className="flex justify-center space-x-4">
                <button
                  onClick={() => handleYesNoResponse('yes')}
                  className={`flex flex-col items-center p-4 rounded-lg transition-all ${
                    responses[currentQuestion]?.value === 'yes'
                      ? 'bg-green-500 text-white scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-green-100'
                  }`}
                >
                  <ThumbsUp className="w-8 h-8 mb-2" />
                  <span className="text-sm font-medium">{t('standalone.survey.answers.yes')}</span>
                </button>
                <button
                  onClick={() => handleYesNoResponse('neutral')}
                  className={`flex flex-col items-center p-4 rounded-lg transition-all ${
                    responses[currentQuestion]?.value === 'neutral'
                      ? 'bg-yellow-500 text-white scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-yellow-100'
                  }`}
                >
                  <Minus className="w-8 h-8 mb-2" />
                  <span className="text-sm font-medium">{t('standalone.survey.answers.neutral')}</span>
                </button>
                <button
                  onClick={() => handleYesNoResponse('no')}
                  className={`flex flex-col items-center p-4 rounded-lg transition-all ${
                    responses[currentQuestion]?.value === 'no'
                      ? 'bg-red-500 text-white scale-105'
                      : 'bg-gray-100 text-gray-600 hover:bg-red-100'
                  }`}
                >
                  <ThumbsDown className="w-8 h-8 mb-2" />
                  <span className="text-sm font-medium">{t('standalone.survey.answers.no')}</span>
                </button>
              </div>
            )}
          </div>

          {/* Notes Section - Shows after all questions */}
          {showNotes && (
            <div className="mb-6">
              <div className="flex items-center mb-3">
                <MessageSquare className="w-5 h-5 text-green-600 mr-2" />
                <h2 className="text-lg font-medium text-gray-900">
                  {tLang(i18n.language, 'Additional Comments', 'Commentaires supplémentaires', 'Comentarios adicionales')}
                </h2>
              </div>
              <p className="text-sm text-gray-500 mb-3">
                {tLang(i18n.language, 'Do you have any comments or suggestions to share? (optional)', 'Avez-vous des commentaires ou suggestions à partager? (optionnel)', 'Do you have any comments or suggestions to share? (optional)')}
              </p>
              <Textarea
                placeholder={tLang(i18n.language, 'Enter your comments here...', 'Entrez vos commentaires ici...', 'Enter your comments here...')}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="min-h-[100px] bg-white border-gray-300 text-gray-900"
              />
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => {
                if (showNotes) {
                  setShowNotes(false);
                } else {
                  setCurrentQuestion(Math.max(0, currentQuestion - 1));
                }
              }}
              disabled={currentQuestion === 0 && !showNotes}
            >
              {t('standalone.survey.back')}
            </Button>
            
            {showNotes ? (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('standalone.survey.submitting')}
                  </>
                ) : (
                  t('standalone.survey.submit')
                )}
              </Button>
            ) : currentQuestion === surveyData?.questions?.length - 1 ? (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setShowNotes(true)}
                disabled={responses[currentQuestion] === null}
              >
                {t('standalone.survey.next')}
              </Button>
            ) : (
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                disabled={responses[currentQuestion] === null}
              >
                {t('standalone.survey.next')}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
